import React, { useState, useEffect } from 'react';
import { useTheme } from '../../theme/ThemeProvider';
import Plotly from 'react-plotly.js';
import { newtonProtocolsAPI } from '../../api';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import ChartElement from './elements/ChartElement';
import TableElement from './elements/TableElement';
import MetricElement from './elements/MetricElement';

const ProtocolEditor = ({ protocol, dataSources, onSave, onCancel, loading }) => {
  const { mode } = useTheme();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    layout_config: {
      elements: []
    },
    data_source_ids: []
  });
  
  const [selectedElement, setSelectedElement] = useState(null);
  const [draggingElement, setDraggingElement] = useState(null);
  const [previewData, setPreviewData] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const [canvasRef, setCanvasRef] = useState(null);
  
  // Persistir estado de paneles en localStorage
  useEffect(() => {
    const savedLeftState = localStorage.getItem('newton-editor-left-panel');
    const savedRightState = localStorage.getItem('newton-editor-right-panel');
    if (savedLeftState !== null) setLeftPanelCollapsed(savedLeftState === 'true');
    if (savedRightState !== null) setRightPanelCollapsed(savedRightState === 'true');
  }, []);
  
  useEffect(() => {
    localStorage.setItem('newton-editor-left-panel', leftPanelCollapsed);
  }, [leftPanelCollapsed]);
  
  useEffect(() => {
    localStorage.setItem('newton-editor-right-panel', rightPanelCollapsed);
  }, [rightPanelCollapsed]);
  
  useEffect(() => {
    if (protocol) {
      setFormData({
        name: protocol.name,
        description: protocol.description || '',
        layout_config: protocol.layout_config || { elements: [] },
        data_source_ids: protocol.data_source_ids || []
      });
    }
  }, [protocol]);
  
  const addElement = (type) => {
    const newElement = {
      id: `${type}_${Date.now()}`,
      type,
      position: { x: 0, y: 0, w: 6, h: 4 },
      config: {
        title: `Nuevo ${type}`,
        dataSource: null,
        ...getDefaultConfig(type)
      }
    };
    
    setFormData(prev => ({
      ...prev,
      layout_config: {
        ...prev.layout_config,
        elements: [...prev.layout_config.elements, newElement]
      }
    }));
    
    setSelectedElement(newElement);
  };
  
  const getDefaultConfig = (type) => {
    switch (type) {
      case 'chart':
        return {
          chartType: 'bar',
          xColumn: '',
          yColumn: '',
          colorColumn: ''
        };
      case 'table':
        return {
          columns: [],
          maxRows: 50,
          calculatedColumns: []
        };
      case 'metric':
        return {
          valueColumn: '',
          aggregation: 'sum',
          prefix: '',
          suffix: ''
        };
      default:
        return {};
    }
  };
  
  const updateElement = (elementId, updates) => {
    setFormData(prev => ({
      ...prev,
      layout_config: {
        ...prev.layout_config,
        elements: prev.layout_config.elements.map(el =>
          el.id === elementId ? { ...el, ...updates } : el
        )
      }
    }));
    
    if (selectedElement?.id === elementId) {
      setSelectedElement(prev => ({ ...prev, ...updates }));
    }
  };
  
  const deleteElement = (elementId) => {
    setFormData(prev => ({
      ...prev,
      layout_config: {
        ...prev.layout_config,
        elements: prev.layout_config.elements.filter(el => el.id !== elementId)
      }
    }));
    
    if (selectedElement?.id === elementId) {
      setSelectedElement(null);
    }
  };
  
  const loadPreviewData = async (sourceId) => {
    if (previewData[sourceId]) return;
    
    // Marcar como cargando
    setPreviewData(prev => ({
      ...prev,
      [sourceId]: { loading: true }
    }));
    
    try {
      const response = await newtonProtocolsAPI.dataSources.getData(sourceId, 100);
      setPreviewData(prev => ({
        ...prev,
        [sourceId]: response.data
      }));
    } catch (err) {
      console.error('Error loading preview data:', err);
      setPreviewData(prev => ({
        ...prev,
        [sourceId]: { error: err.message || 'Error cargando datos' }
      }));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('El nombre del protocolo es requerido');
      return;
    }
    
    try {
      await onSave(formData);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error guardando el protocolo');
    }
  };
  
  // Handler para cambios en el layout (drag/resize)
  const handleLayoutChange = (layout) => {
    if (isDragging) return; // Evitar actualizaciones durante drag
    
    setFormData(prev => ({
      ...prev,
      layout_config: {
        ...prev.layout_config,
        elements: prev.layout_config.elements.map(el => {
          const layoutItem = layout.find(l => l.i === el.id);
          if (layoutItem) {
            return {
              ...el,
              position: {
                x: layoutItem.x,
                y: layoutItem.y,
                w: layoutItem.w,
                h: layoutItem.h
              }
            };
          }
          return el;
        })
      }
    }));
  };
  
  // Generar layout para react-grid-layout
  const generateLayout = () => {
    return formData.layout_config.elements.map(el => ({
      i: el.id,
      x: el.position.x,
      y: el.position.y,
      w: el.position.w,
      h: el.position.h,
      minW: 2,
      minH: 2,
      maxW: 12,
      maxH: 12
    }));
  };
  
  const renderElement = (element) => {
    const data = element.config.dataSource ? previewData[element.config.dataSource] : null;
    
    switch (element.type) {
      case 'chart':
        return (
          <ChartElement 
            element={element} 
            data={data}
            onUpdate={(updates) => updateElement(element.id, updates)}
            isEditing={true}
          />
        );
      case 'table':
        return (
          <TableElement 
            element={element} 
            data={data}
            onUpdate={(updates) => updateElement(element.id, updates)}
            isEditing={true}
          />
        );
      case 'metric':
        return (
          <MetricElement 
            element={element} 
            data={data}
            onUpdate={(updates) => updateElement(element.id, updates)}
            isEditing={true}
          />
        );
      default:
        return <div>Elemento desconocido</div>;
    }
  };
  
  const renderChart = (element, data) => {
    if (!data || !data.rows || data.rows.length === 0) {
      return (
        <div style={styles.placeholderContent}>
          <div>📊</div>
          <div>Selecciona una fuente de datos para ver el gráfico</div>
        </div>
      );
    }
    
    const { chartType, xColumn, yColumn } = element.config;
    
    if (!xColumn || !yColumn) {
      return (
        <div style={styles.placeholderContent}>
          <div>⚙️</div>
          <div>Configura las columnas X e Y</div>
        </div>
      );
    }
    
    try {
      const plotData = [{
        x: data.rows.map(row => row[xColumn]),
        y: data.rows.map(row => row[yColumn]),
        type: chartType,
        marker: { color: mode === 'dark' ? '#29B5E8' : '#1B96C7' }
      }];
      
      const layout = {
        paper_bgcolor: mode === 'dark' ? '#1a1f2e' : '#ffffff',
        plot_bgcolor: mode === 'dark' ? '#151a21' : '#f5f7fa',
        font: { color: mode === 'dark' ? '#f6f8fb' : '#1a202c' },
        margin: { t: 40, r: 20, b: 40, l: 50 },
        height: 250
      };
      
      return <Plotly data={plotData} layout={layout} config={{ displayModeBar: false }} />;
    } catch (err) {
      return (
        <div style={styles.placeholderContent}>
          <div>⚠️</div>
          <div>Error renderizando gráfico</div>
        </div>
      );
    }
  };
  
  const renderTable = (element, data) => {
    if (!data || !data.rows || data.rows.length === 0) {
      return (
        <div style={styles.placeholderContent}>
          <div>📋</div>
          <div>Selecciona una fuente de datos para ver la tabla</div>
        </div>
      );
    }
    
    const columns = element.config.columns.length > 0
      ? element.config.columns
      : data.columns.slice(0, 5);
    
    const rows = data.rows.slice(0, element.config.maxRows || 10);
    
    return (
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              {columns.map((col, idx) => (
                <th key={idx} style={styles.th}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx}>
                {columns.map((col, colIdx) => (
                  <td key={colIdx} style={styles.td}>{row[col]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
  
  const renderMetric = (element, data) => {
    if (!data || !data.rows || data.rows.length === 0) {
      return (
        <div style={styles.placeholderContent}>
          <div>📈</div>
          <div>Selecciona una fuente de datos</div>
        </div>
      );
    }
    
    const { valueColumn, aggregation, prefix, suffix } = element.config;
    
    if (!valueColumn) {
      return (
        <div style={styles.placeholderContent}>
          <div>⚙️</div>
          <div>Configura la columna de valor</div>
        </div>
      );
    }
    
    let value = 0;
    const values = data.rows.map(row => parseFloat(row[valueColumn]) || 0);
    
    switch (aggregation) {
      case 'sum':
        value = values.reduce((a, b) => a + b, 0);
        break;
      case 'avg':
        value = values.reduce((a, b) => a + b, 0) / values.length;
        break;
      case 'min':
        value = Math.min(...values);
        break;
      case 'max':
        value = Math.max(...values);
        break;
      case 'count':
        value = values.length;
        break;
      default:
        value = 0;
    }
    
    return (
      <div style={styles.metricContent}>
        <div style={styles.metricValue}>
          {prefix}{value.toLocaleString()}{suffix}
        </div>
      </div>
    );
  };
  
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      backgroundColor: mode === 'dark' ? 'var(--dark-bg-primary)' : 'var(--color-light-gray)'
    },
    header: {
      backgroundColor: mode === 'dark' ? 'var(--dark-bg-secondary)' : 'var(--color-white)',
      borderBottom: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`,
      padding: 'var(--spacing-lg) var(--spacing-xl)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--spacing-lg)'
    },
    title: {
      fontSize: 'var(--font-size-2xl)',
      fontWeight: 'var(--font-weight-bold)',
      color: mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)'
    },
    headerRight: {
      display: 'flex',
      gap: 'var(--spacing-md)'
    },
    main: {
      display: 'flex',
      flex: 1,
      overflow: 'hidden'
    },
    toolbar: {
      width: leftPanelCollapsed ? '60px' : '280px',
      backgroundColor: mode === 'dark' ? 'var(--dark-bg-secondary)' : 'var(--color-white)',
      borderRight: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`,
      overflowY: 'auto',
      overflowX: 'hidden',
      padding: leftPanelCollapsed ? 'var(--spacing-sm)' : 'var(--spacing-lg)',
      transition: 'width 0.3s ease, padding 0.3s ease',
      display: 'flex',
      flexDirection: 'column'
    },
    canvas: {
      flex: 1,
      padding: 'var(--spacing-md)',
      overflowY: 'auto',
      position: 'relative'
    },
    properties: {
      width: rightPanelCollapsed ? '60px' : '360px',
      backgroundColor: mode === 'dark' ? 'var(--dark-bg-secondary)' : 'var(--color-white)',
      borderLeft: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`,
      overflowY: 'auto',
      overflowX: 'hidden',
      padding: rightPanelCollapsed ? 'var(--spacing-sm)' : 'var(--spacing-lg)',
      transition: 'width 0.3s ease, padding 0.3s ease',
      display: 'flex',
      flexDirection: 'column'
    },
    collapseButton: {
      padding: 'var(--spacing-sm)',
      backgroundColor: mode === 'dark' ? 'var(--dark-bg-tertiary)' : 'var(--color-light-gray)',
      border: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-medium-gray)'}`,
      borderRadius: 'var(--border-radius-md)',
      color: mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)',
      fontSize: 'var(--font-size-lg)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      marginBottom: 'var(--spacing-md)',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    section: {
      marginBottom: 'var(--spacing-xl)'
    },
    sectionTitle: {
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-semibold)',
      color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-medium-gray)',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginBottom: 'var(--spacing-md)'
    },
    toolButton: {
      width: '100%',
      padding: 'var(--spacing-md)',
      marginBottom: 'var(--spacing-sm)',
      backgroundColor: mode === 'dark' ? 'var(--dark-bg-tertiary)' : 'var(--color-white)',
      border: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`,
      borderRadius: 'var(--border-radius-md)',
      color: mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)',
      fontSize: 'var(--font-size-base)',
      textAlign: 'left',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--spacing-sm)'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(12, 1fr)',
      gap: 'var(--spacing-md)',
      minHeight: '800px'
    },
    element: {
      backgroundColor: mode === 'dark' ? 'var(--dark-bg-secondary)' : 'var(--color-white)',
      borderRadius: 'var(--border-radius-lg)',
      border: `2px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`,
      padding: 'var(--spacing-md)',
      position: 'relative',
      cursor: 'move',
      transition: 'border-color 0.2s ease'
    },
    elementSelected: {
      borderColor: '#29B5E8',
      boxShadow: '0 0 0 3px rgba(41, 181, 232, 0.1)'
    },
    elementHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 'var(--spacing-sm)',
      paddingBottom: 'var(--spacing-sm)',
      borderBottom: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`,
      cursor: 'default'
    },
    elementTitle: {
      fontSize: 'var(--font-size-base)',
      fontWeight: 'var(--font-weight-semibold)',
      color: mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)'
    },
    elementActions: {
      display: 'flex',
      gap: 'var(--spacing-xs)'
    },
    actionButton: {
      padding: 'var(--spacing-xs)',
      backgroundColor: 'transparent',
      border: 'none',
      cursor: 'pointer',
      fontSize: 'var(--font-size-lg)',
      opacity: 0.6,
      transition: 'opacity 0.2s ease'
    },
    formGroup: {
      marginBottom: 'var(--spacing-md)'
    },
    label: {
      display: 'block',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-semibold)',
      color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-dark-gray)',
      marginBottom: 'var(--spacing-xs)'
    },
    input: {
      width: '100%',
      padding: 'var(--spacing-sm)',
      fontSize: 'var(--font-size-base)',
      borderRadius: 'var(--border-radius-md)',
      border: `1px solid ${mode === 'dark' ? 'var(--dark-input-border)' : 'var(--color-light-gray)'}`,
      backgroundColor: mode === 'dark' ? 'var(--dark-input-bg)' : 'var(--color-white)',
      color: mode === 'dark' ? 'var(--dark-input-text)' : 'var(--color-black)',
      boxSizing: 'border-box'
    },
    placeholderContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 'var(--spacing-sm)',
      padding: 'var(--spacing-xl)',
      color: mode === 'dark' ? 'var(--dark-text-tertiary)' : 'var(--color-medium-gray)',
      fontSize: 'var(--font-size-3xl)',
      textAlign: 'center',
      minHeight: '200px'
    },
    tableContainer: {
      overflowX: 'auto',
      maxHeight: '300px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: 'var(--font-size-sm)'
    },
    th: {
      textAlign: 'left',
      padding: 'var(--spacing-sm)',
      backgroundColor: mode === 'dark' ? 'var(--dark-bg-tertiary)' : 'var(--color-light-gray)',
      color: mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)',
      fontWeight: 'var(--font-weight-semibold)',
      borderBottom: `2px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-medium-gray)'}`,
      position: 'sticky',
      top: 0
    },
    td: {
      padding: 'var(--spacing-sm)',
      borderBottom: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`,
      color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-dark-gray)'
    },
    metricContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '150px'
    },
    metricValue: {
      fontSize: 'var(--font-size-5xl)',
      fontWeight: 'var(--font-weight-bold)',
      color: '#29B5E8'
    }
  };
  
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button className="btn btn-ghost" onClick={onCancel}>
            ← Volver
          </button>
          <div>
            <h1 style={styles.title}>
              {protocol ? `Editando: ${protocol.name}` : 'Nuevo Protocolo'}
            </h1>
          </div>
        </div>
        <div style={styles.headerRight}>
          <button className="btn btn-secondary" onClick={onCancel} disabled={loading}>
            Cancelar
          </button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
            {loading ? '⏳ Guardando...' : '💾 Guardar Protocolo'}
          </button>
        </div>
      </div>
      
      <div style={styles.main}>
        {/* Toolbar - Herramientas */}
        <div style={styles.toolbar}>
          {/* Botón de colapsar */}
          <button
            style={styles.collapseButton}
            onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
            title={leftPanelCollapsed ? 'Expandir panel' : 'Colapsar panel'}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#29B5E8';
              e.currentTarget.style.color = '#FFFFFF';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = mode === 'dark' ? 'var(--dark-bg-tertiary)' : 'var(--color-light-gray)';
              e.currentTarget.style.color = mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)';
            }}
          >
            {leftPanelCollapsed ? '▶️' : '◀️'}
          </button>
          
          {!leftPanelCollapsed && (
            <>
              <div style={styles.section}>
                <div style={styles.sectionTitle}>Información</div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Nombre *</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Mi Protocolo"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Descripción</label>
                  <textarea
                    style={{...styles.input, minHeight: '60px', resize: 'vertical'}}
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Descripción del protocolo..."
                  />
                </div>
              </div>
              
              <div style={styles.section}>
                <div style={styles.sectionTitle}>Elementos de Visualización</div>
                <button style={styles.toolButton} onClick={() => addElement('chart')}>
                  <span>📊</span>
                  <span>Gráfico</span>
                </button>
                <button style={styles.toolButton} onClick={() => addElement('table')}>
                  <span>📋</span>
                  <span>Tabla</span>
                </button>
                <button style={styles.toolButton} onClick={() => addElement('metric')}>
                  <span>📈</span>
                  <span>Métrica</span>
                </button>
              </div>
            </>
          )}
          
          {leftPanelCollapsed && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)', marginTop: 'var(--spacing-md)' }}>
              <button
                style={{
                  ...styles.toolButton,
                  padding: 'var(--spacing-md) var(--spacing-sm)',
                  justifyContent: 'center'
                }}
                onClick={() => addElement('chart')}
                title="Añadir Gráfico"
              >
                <span style={{ fontSize: '24px' }}>📊</span>
              </button>
              <button
                style={{
                  ...styles.toolButton,
                  padding: 'var(--spacing-md) var(--spacing-sm)',
                  justifyContent: 'center'
                }}
                onClick={() => addElement('table')}
                title="Añadir Tabla"
              >
                <span style={{ fontSize: '24px' }}>📋</span>
              </button>
              <button
                style={{
                  ...styles.toolButton,
                  padding: 'var(--spacing-md) var(--spacing-sm)',
                  justifyContent: 'center'
                }}
                onClick={() => addElement('metric')}
                title="Añadir Métrica"
              >
                <span style={{ fontSize: '24px' }}>📈</span>
              </button>
            </div>
          )}
        </div>
        
        {/* Canvas - Área de trabajo */}
        <div style={styles.canvas} ref={(el) => el && setCanvasRef(el)}>
          {formData.layout_config.elements.length === 0 ? (
            <div style={{...styles.placeholderContent, minHeight: '400px'}}>
              <div>👈</div>
              <div>Agrega elementos desde el panel izquierdo</div>
            </div>
          ) : (
            <GridLayout
              className="layout"
              layout={generateLayout()}
              cols={12}
              rowHeight={60}
              width={canvasRef ? canvasRef.offsetWidth - 32 : 1200}
              onLayoutChange={handleLayoutChange}
              onDragStart={() => setIsDragging(true)}
              onDragStop={() => setIsDragging(false)}
              onResizeStart={() => setIsDragging(true)}
              onResizeStop={() => setIsDragging(false)}
              isDraggable={true}
              isResizable={true}
              compactType="vertical"
              preventCollision={false}
              style={{
                minHeight: '800px',
                background: mode === 'dark' 
                  ? 'repeating-linear-gradient(0deg, transparent, transparent 59px, rgba(41, 181, 232, 0.05) 59px, rgba(41, 181, 232, 0.05) 60px), repeating-linear-gradient(90deg, transparent, transparent calc(100% / 12 - 1px), rgba(41, 181, 232, 0.05) calc(100% / 12 - 1px), rgba(41, 181, 232, 0.05) calc(100% / 12))' 
                  : 'repeating-linear-gradient(0deg, transparent, transparent 59px, rgba(41, 181, 232, 0.1) 59px, rgba(41, 181, 232, 0.1) 60px), repeating-linear-gradient(90deg, transparent, transparent calc(100% / 12 - 1px), rgba(41, 181, 232, 0.1) calc(100% / 12 - 1px), rgba(41, 181, 232, 0.1) calc(100% / 12))',
                padding: 'var(--spacing-md)'
              }}
            >
              {formData.layout_config.elements.map((element) => {
                return (
                  <div
                    key={element.id}
                    style={{
                      ...styles.element,
                      ...(selectedElement?.id === element.id ? styles.elementSelected : {}),
                      ...(isDragging ? { cursor: 'grabbing' } : {}),
                      overflow: 'hidden'
                    }}
                    onClick={() => setSelectedElement(element)}
                  >
                    <div style={styles.elementHeader}>
                      <div style={styles.elementTitle}>{element.config.title}</div>
                      <div style={styles.elementActions}>
                        <button
                          style={styles.actionButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteElement(element.id);
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    <div style={{ overflowY: 'auto', height: 'calc(100% - 48px)' }}>
                      {renderElement(element)}
                    </div>
                  </div>
                );
              })}
            </GridLayout>
          )}
        </div>
        
        {/* Properties - Propiedades del elemento seleccionado */}
        <div style={styles.properties}>
          {/* Botón de colapsar */}
          <button
            style={styles.collapseButton}
            onClick={() => setRightPanelCollapsed(!rightPanelCollapsed)}
            title={rightPanelCollapsed ? 'Expandir propiedades' : 'Colapsar propiedades'}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#29B5E8';
              e.currentTarget.style.color = '#FFFFFF';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = mode === 'dark' ? 'var(--dark-bg-tertiary)' : 'var(--color-light-gray)';
              e.currentTarget.style.color = mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)';
            }}
          >
            {rightPanelCollapsed ? '◀️' : '▶️'}
          </button>
          
          {!rightPanelCollapsed && selectedElement ? (
            <>
              <div style={{...styles.sectionTitle, fontSize: 'var(--font-size-lg)', marginBottom: 'var(--spacing-lg)'}}>
                ⚙️ Propiedades del Elemento
              </div>
              
              <div style={styles.formGroup}>
                <label style={{...styles.label, fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-bold)'}}>
                  📝 Título
                </label>
                <input
                  type="text"
                  style={styles.input}
                  value={selectedElement.config.title}
                  onChange={(e) => updateElement(selectedElement.id, {
                    config: { ...selectedElement.config, title: e.target.value }
                  })}
                />
              </div>
              
              <div style={styles.formGroup}>
                <label style={{...styles.label, fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-bold)'}}>
                  🔌 Fuente de Datos
                </label>
                <select
                  style={styles.input}
                  value={selectedElement.config.dataSource || ''}
                  onChange={(e) => {
                    const sourceId = parseInt(e.target.value);
                    updateElement(selectedElement.id, {
                      config: { ...selectedElement.config, dataSource: sourceId }
                    });
                    if (sourceId) loadPreviewData(sourceId);
                  }}
                >
                  <option value="">Seleccionar fuente...</option>
                  {dataSources.map(source => (
                    <option key={source.id} value={source.id}>{source.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Configuración específica por tipo */}
              {selectedElement.type === 'chart' && (
                <>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Tipo de Gráfico</label>
                    <select
                      style={styles.input}
                      value={selectedElement.config.chartType}
                      onChange={(e) => updateElement(selectedElement.id, {
                        config: { ...selectedElement.config, chartType: e.target.value }
                      })}
                    >
                      <option value="bar">Barras</option>
                      <option value="line">Líneas</option>
                      <option value="scatter">Dispersión</option>
                      <option value="pie">Pastel</option>
                      <option value="area">Área</option>
                      <option value="heatmap">Mapa de Calor</option>
                      <option value="box">Caja y Bigotes</option>
                    </select>
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Agregación</label>
                    <select
                      style={styles.input}
                      value={selectedElement.config.aggregation || 'none'}
                      onChange={(e) => updateElement(selectedElement.id, {
                        config: { ...selectedElement.config, aggregation: e.target.value }
                      })}
                    >
                      <option value="none">Sin agregación</option>
                      <option value="sum">Suma</option>
                      <option value="avg">Promedio</option>
                      <option value="min">Mínimo</option>
                      <option value="max">Máximo</option>
                      <option value="count">Conteo</option>
                    </select>
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Ordenar por</label>
                    <select
                      style={styles.input}
                      value={selectedElement.config.sortBy || 'none'}
                      onChange={(e) => updateElement(selectedElement.id, {
                        config: { ...selectedElement.config, sortBy: e.target.value }
                      })}
                    >
                      <option value="none">Sin ordenar</option>
                      <option value="x">Eje X</option>
                      <option value="y">Eje Y</option>
                    </select>
                  </div>
                  
                  {selectedElement.config.sortBy !== 'none' && (
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Dirección</label>
                      <select
                        style={styles.input}
                        value={selectedElement.config.sortOrder || 'asc'}
                        onChange={(e) => updateElement(selectedElement.id, {
                          config: { ...selectedElement.config, sortOrder: e.target.value }
                        })}
                      >
                        <option value="asc">Ascendente</option>
                        <option value="desc">Descendente</option>
                      </select>
                    </div>
                  )}
                  
                  {selectedElement.config.dataSource && (
                    <>
                      {previewData[selectedElement.config.dataSource]?.loading && (
                        <div style={{
                          padding: 'var(--spacing-md)',
                          textAlign: 'center',
                          color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-medium-gray)',
                          fontSize: 'var(--font-size-sm)'
                        }}>
                          ⏳ Cargando columnas disponibles...
                        </div>
                      )}
                      
                      {previewData[selectedElement.config.dataSource]?.error && (
                        <div style={{
                          padding: 'var(--spacing-md)',
                          textAlign: 'center',
                          color: '#FF6B35',
                          fontSize: 'var(--font-size-sm)'
                        }}>
                          ⚠️ Error: {previewData[selectedElement.config.dataSource].error}
                        </div>
                      )}
                      
                      {previewData[selectedElement.config.dataSource]?.columns && (
                        <>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>Columna X</label>
                            <select
                              style={styles.input}
                              value={selectedElement.config.xColumn || ''}
                              onChange={(e) => updateElement(selectedElement.id, {
                                config: { ...selectedElement.config, xColumn: e.target.value }
                              })}
                            >
                              <option value="">Seleccionar columna...</option>
                              {previewData[selectedElement.config.dataSource].columns.map(col => (
                                <option key={col} value={col}>{col}</option>
                              ))}
                            </select>
                          </div>
                          
                          <div style={styles.formGroup}>
                            <label style={styles.label}>Columna Y</label>
                            <select
                              style={styles.input}
                              value={selectedElement.config.yColumn || ''}
                              onChange={(e) => updateElement(selectedElement.id, {
                                config: { ...selectedElement.config, yColumn: e.target.value }
                              })}
                            >
                              <option value="">Seleccionar columna...</option>
                              {previewData[selectedElement.config.dataSource].columns.map(col => (
                                <option key={col} value={col}>{col}</option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
              
              {selectedElement.type === 'table' && (
                <>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Filas Máximas</label>
                    <input
                      type="number"
                      style={styles.input}
                      value={selectedElement.config.maxRows || 50}
                      onChange={(e) => updateElement(selectedElement.id, {
                        config: { ...selectedElement.config, maxRows: parseInt(e.target.value) || 50 }
                      })}
                      min="1"
                      max="1000"
                    />
                  </div>
                  
                  {selectedElement.config.dataSource && previewData[selectedElement.config.dataSource]?.columns && (
                    <>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Columnas a Mostrar</label>
                        <div style={{
                          maxHeight: '200px',
                          overflowY: 'auto',
                          border: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`,
                          borderRadius: 'var(--border-radius-md)',
                          padding: 'var(--spacing-sm)',
                          backgroundColor: mode === 'dark' ? 'var(--dark-bg-secondary)' : 'var(--color-white)'
                        }}>
                          {previewData[selectedElement.config.dataSource].columns.map(col => (
                            <label key={col} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--spacing-sm)',
                              padding: 'var(--spacing-xs)',
                              cursor: 'pointer',
                              fontSize: 'var(--font-size-sm)'
                            }}>
                              <input
                                type="checkbox"
                                checked={(selectedElement.config.columns || []).includes(col)}
                                onChange={(e) => {
                                  const currentColumns = selectedElement.config.columns || [];
                                  const newColumns = e.target.checked
                                    ? [...currentColumns, col]
                                    : currentColumns.filter(c => c !== col);
                                  updateElement(selectedElement.id, {
                                    config: { ...selectedElement.config, columns: newColumns }
                                  });
                                }}
                              />
                              <span>{col}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      
                      {/* Editor de Columnas Calculadas */}
                      <div style={styles.formGroup}>
                        <label style={{...styles.label, fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-bold)'}}>
                          🧮 Columnas Calculadas
                        </label>
                        
                        <div style={{
                          border: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`,
                          borderRadius: 'var(--border-radius-md)',
                          padding: 'var(--spacing-md)',
                          backgroundColor: mode === 'dark' ? 'var(--dark-bg-secondary)' : 'var(--color-white)'
                        }}>
                          {(selectedElement.config.calculatedColumns || []).map((calcCol, index) => (
                            <div key={index} style={{
                              display: 'flex',
                              gap: 'var(--spacing-sm)',
                              alignItems: 'center',
                              marginBottom: 'var(--spacing-md)',
                              padding: 'var(--spacing-sm)',
                              border: `1px solid ${mode === 'dark' ? 'var(--dark-border-secondary)' : 'var(--color-light-gray)'}`,
                              borderRadius: 'var(--border-radius-sm)',
                              backgroundColor: mode === 'dark' ? 'var(--dark-bg-tertiary)' : '#F8F9FA'
                            }}>
                              <div style={{ flex: 1 }}>
                                <input
                                  type="text"
                                  placeholder="Nombre de columna"
                                  value={calcCol.name || ''}
                                  onChange={(e) => {
                                    const newCalcCols = [...(selectedElement.config.calculatedColumns || [])];
                                    newCalcCols[index].name = e.target.value;
                                    updateElement(selectedElement.id, {
                                      config: { ...selectedElement.config, calculatedColumns: newCalcCols }
                                    });
                                  }}
                                  style={{
                                    ...styles.input,
                                    fontSize: 'var(--font-size-sm)',
                                    marginBottom: 'var(--spacing-xs)'
                                  }}
                                />
                                <input
                                  type="text"
                                  placeholder="Expresión (ej: ([pred] - [u_2024]) / [u_2024] * 100)"
                                  value={calcCol.expression || ''}
                                  onChange={(e) => {
                                    const newCalcCols = [...(selectedElement.config.calculatedColumns || [])];
                                    newCalcCols[index].expression = e.target.value;
                                    updateElement(selectedElement.id, {
                                      config: { ...selectedElement.config, calculatedColumns: newCalcCols }
                                    });
                                  }}
                                  style={{
                                    ...styles.input,
                                    fontSize: 'var(--font-size-sm)'
                                  }}
                                />
                              </div>
                              <button
                                onClick={() => {
                                  const newCalcCols = (selectedElement.config.calculatedColumns || []).filter((_, i) => i !== index);
                                  updateElement(selectedElement.id, {
                                    config: { ...selectedElement.config, calculatedColumns: newCalcCols }
                                  });
                                }}
                                style={{
                                  padding: 'var(--spacing-xs)',
                                  border: 'none',
                                  backgroundColor: '#FF6B35',
                                  color: 'white',
                                  borderRadius: 'var(--border-radius-sm)',
                                  cursor: 'pointer',
                                  fontSize: 'var(--font-size-sm)'
                                }}
                                title="Eliminar columna calculada"
                              >
                                🗑️
                              </button>
                            </div>
                          ))}
                          
                          <button
                            onClick={() => {
                              const newCalcCols = [...(selectedElement.config.calculatedColumns || []), { name: '', expression: '' }];
                              updateElement(selectedElement.id, {
                                config: { ...selectedElement.config, calculatedColumns: newCalcCols }
                              });
                            }}
                            style={{
                              padding: 'var(--spacing-sm) var(--spacing-md)',
                              border: 'none',
                              backgroundColor: '#29B5E8',
                              color: 'white',
                              borderRadius: 'var(--border-radius-sm)',
                              cursor: 'pointer',
                              fontSize: 'var(--font-size-sm)',
                              fontWeight: 'var(--font-weight-medium)'
                            }}
                          >
                            + Agregar Columna Calculada
                          </button>
                          
                          <div style={{
                            marginTop: 'var(--spacing-md)',
                            padding: 'var(--spacing-sm)',
                            backgroundColor: mode === 'dark' ? 'rgba(41, 181, 232, 0.1)' : 'rgba(41, 181, 232, 0.05)',
                            borderRadius: 'var(--border-radius-sm)',
                            fontSize: 'var(--font-size-xs)',
                            color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-medium-gray)'
                          }}>
                            <div style={{ fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--spacing-xs)' }}>
                              💡 Ejemplos de expresiones:
                            </div>
                            <div>• Variación: <code>([pred] - [u_2024]) / [u_2024] * 100</code></div>
                            <div>• Tendencia: <code>If([pred] > [u_2024], 'Crecimiento', 'Decrecimiento')</code></div>
                            <div>• Promedio: <code>([u_2024] + [u_2023] + [u_2022]) / 3</code></div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
              
              {selectedElement.type === 'metric' && selectedElement.config.dataSource && (
                <>
                  {previewData[selectedElement.config.dataSource]?.loading && (
                    <div style={{
                      padding: 'var(--spacing-md)',
                      textAlign: 'center',
                      color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-medium-gray)',
                      fontSize: 'var(--font-size-sm)'
                    }}>
                      ⏳ Cargando columnas disponibles...
                    </div>
                  )}
                  
                  {previewData[selectedElement.config.dataSource]?.error && (
                    <div style={{
                      padding: 'var(--spacing-md)',
                      textAlign: 'center',
                      color: '#FF6B35',
                      fontSize: 'var(--font-size-sm)'
                    }}>
                      ⚠️ Error: {previewData[selectedElement.config.dataSource].error}
                    </div>
                  )}
                  
                  {previewData[selectedElement.config.dataSource]?.columns && (
                    <>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Columna de Valor</label>
                        <select
                          style={styles.input}
                          value={selectedElement.config.valueColumn || ''}
                          onChange={(e) => updateElement(selectedElement.id, {
                            config: { ...selectedElement.config, valueColumn: e.target.value }
                          })}
                        >
                          <option value="">Seleccionar columna...</option>
                          {previewData[selectedElement.config.dataSource].columns.map(col => (
                            <option key={col} value={col}>{col}</option>
                          ))}
                        </select>
                      </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Agregación</label>
                    <select
                      style={styles.input}
                      value={selectedElement.config.aggregation}
                      onChange={(e) => updateElement(selectedElement.id, {
                        config: { ...selectedElement.config, aggregation: e.target.value }
                      })}
                    >
                      <option value="sum">Suma</option>
                      <option value="avg">Promedio</option>
                      <option value="min">Mínimo</option>
                      <option value="max">Máximo</option>
                      <option value="count">Conteo</option>
                    </select>
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Prefijo (opcional)</label>
                    <input
                      type="text"
                      style={styles.input}
                      value={selectedElement.config.prefix || ''}
                      onChange={(e) => updateElement(selectedElement.id, {
                        config: { ...selectedElement.config, prefix: e.target.value }
                      })}
                      placeholder="$"
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Sufijo (opcional)</label>
                    <input
                      type="text"
                      style={styles.input}
                      value={selectedElement.config.suffix || ''}
                      onChange={(e) => updateElement(selectedElement.id, {
                        config: { ...selectedElement.config, suffix: e.target.value }
                      })}
                      placeholder="%"
                    />
                  </div>
                    </>
                  )}
                </>
              )}
            </>
          ) : !rightPanelCollapsed ? (
            <div style={{...styles.placeholderContent, minHeight: '200px'}}>
              <div>👆</div>
              <div>Selecciona un elemento para ver sus propiedades</div>
            </div>
          ) : null}
          
          {rightPanelCollapsed && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              color: mode === 'dark' ? 'var(--dark-text-tertiary)' : 'var(--color-medium-gray)',
              fontSize: '32px'
            }}>
              ⚙️
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProtocolEditor;

